import { useRef, useState } from "react";
import { Loader2, PartyPopper, Shell } from "lucide-react";
import { toast } from "sonner";
import { api, USER_ID } from "../api";
import { badgeToast, melaToast, MelaCoin } from "./BadgesSheet";
import { coinRain } from "./coinRain";

const HOWTO_KEY = "xp_howto_ludo";
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const reduced = () => window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

let actx = null;
const sfxOn = () => localStorage.getItem("xp_sound_on") !== "0";
const actxGet = () => (actx = actx || new (window.AudioContext || window.webkitAudioContext)());
const shellClatter = () => {
  if (!sfxOn()) return;
  try {
    const c = actxGet();
    for (let i = 0; i < 6; i++) {
      const t = c.currentTime + i * 0.055 + Math.random() * 0.02;
      const o = c.createOscillator();
      const g = c.createGain();
      o.type = "triangle";
      o.frequency.setValueAtTime(1300 + Math.random() * 1700, t);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(0.05, t + 0.005);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
      o.connect(g).connect(c.destination);
      o.start(t);
      o.stop(t + 0.08);
    }
  } catch { /* audio unavailable */ }
};
const beadTap = () => {
  if (!sfxOn()) return;
  try {
    const c = actxGet();
    const t = c.currentTime;
    const o = c.createOscillator();
    const g = c.createGain();
    o.type = "sine";
    o.frequency.setValueAtTime(540, t);
    o.frequency.exponentialRampToValueAtTime(300, t + 0.07);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(0.08, t + 0.006);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.09);
    o.connect(g).connect(c.destination);
    o.start(t);
    o.stop(t + 0.1);
  } catch { /* audio unavailable */ }
};

const FOODS = [
  { name: "Samosa", pa: "ਸਮੋਸਾ", color: "#E3B23C", dark: "#8a6716" },
  { name: "Chai", pa: "ਚਾਹ", color: "#C0392B", dark: "#6e1e14" },
  { name: "Jalebi", pa: "ਜਲੇਬੀ", color: "#E67E22", dark: "#8a4a10" },
  { name: "Kulfi", pa: "ਕੁਲਫ਼ੀ", color: "#27825C", dark: "#14492f" },
];

const rotc = ([r, c], t) => { let p = [r, c]; for (let i = 0; i < t; i++) p = [p[1], 4 - p[0]]; return p; };
const BASE = [
  [0, 2], [0, 1], [0, 0], [1, 0], [2, 0], [3, 0], [4, 0], [4, 1],
  [4, 2], [4, 3], [4, 4], [3, 4], [2, 4], [1, 4], [0, 4], [0, 3],
  [1, 2], [1, 3], [2, 3], [3, 3], [3, 2], [3, 1], [2, 1], [1, 1],
  [2, 2],
];
const ROT = [2, 0, 3, 1]; // player sides: bottom, top, left, right
const PATHS = ROT.map((t) => BASE.map((cell) => rotc(cell, t)));
const HOME = 24;
const SAFE = new Set(["0,2", "2,0", "2,4", "4,2", "1,1", "1,3", "3,1", "3,3", "2,2"]);
const START_KEYS = PATHS.map((p) => `${p[0][0]},${p[0][1]}`);
const ROLL_NAME = { 1: "ਇੱਕ · 1", 2: "ਦੋ · 2", 3: "ਤਿੰਨ · 3", 4: "ਚੌਕਾ · CHAMMA 4!", 8: "ਅੱਠ · ASHTA 8!" };

const Kaudi = ({ up, rolling }) => (
  <span className={`kaudi-shell ${up ? "kaudi-up" : "kaudi-down"} ${rolling ? "kaudi-rolling" : ""}`} aria-hidden="true">
    {up && <span className="kaudi-mouth" />}
  </span>
);

export const PunjabiLudo = ({ refresh }) => {
  const [howto, setHowto] = useState(() => localStorage.getItem(HOWTO_KEY) !== "1");
  const [phase, setPhase] = useState("setup");
  const [count, setCount] = useState(2);
  const [beadCount, setBeadCount] = useState(4);
  const [mode, setMode] = useState("single");
  const [score, setScore] = useState([]);
  const scoreRef = useRef([]);
  const [beads, setBeads] = useState([]);
  const beadRef = useRef([]);
  const [turn, setTurn] = useState(0);
  const [shells, setShells] = useState([true, false, true, false]);
  const [rollVal, setRollVal] = useState(null);
  const [rolling, setRolling] = useState(false);
  const [pending, setPending] = useState(null); // {v, movable:[idx]}
  const [anim, setAnim] = useState(false);
  const [busy, setBusy] = useState(false);
  const [winner, setWinner] = useState(null);
  const [reward, setReward] = useState(null);
  const session = useRef(null);

  const start = async () => {
    setBusy(true);
    try {
      const r = await api.post("/arcade/session", { user_id: USER_ID, game: "ludo" });
      session.current = r.data.session_id;
      beadRef.current = Array.from({ length: count }, () => Array(beadCount).fill(0));
      setBeads(beadRef.current.map((t) => [...t]));
      scoreRef.current = Array(count).fill(0);
      setScore([...scoreRef.current]);
      setTurn(0); setRollVal(null); setPending(null); setWinner(null); setReward(null);
      setPhase("playing");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not lay out the board"); }
    setBusy(false);
  };

  const nextRound = async () => {
    setBusy(true);
    try {
      const r = await api.post("/arcade/session", { user_id: USER_ID, game: "ludo" });
      session.current = r.data.session_id;
      beadRef.current = Array.from({ length: count }, () => Array(beadCount).fill(0));
      setBeads(beadRef.current.map((t) => [...t]));
      setTurn(scoreRef.current.reduce((a, b) => a + b, 0) % count);
      setRollVal(null); setPending(null); setWinner(null); setReward(null);
      setPhase("playing");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start the next round"); }
    setBusy(false);
  };

  const finishOnServer = async (winnerName, matchWon = false) => {
    try {
      const rounds = scoreRef.current.reduce((a, b) => a + (b || 0), 0);
      const r = await api.post("/arcade/ludo/finish", {
        user_id: USER_ID, session_id: session.current, players: count, winner: winnerName,
        mode, match_won: matchWon, match_score: matchWon ? `2-${Math.min(2, Math.max(0, rounds - 2))}` : "" });
      setReward(r.data.mela_result);
      badgeToast(r.data.new_badges);
      refresh();
    } catch (e) { setReward({ error: true, message: e.response?.data?.detail || "" }); }
  };

  const moveBead = async (bi, v) => {
    setAnim(true);
    setPending(null);
    const fast = reduced();
    const bs = beadRef.current;
    const target = bs[turn][bi] + v;
    for (let s = bs[turn][bi] + 1; s <= target; s++) {
      bs[turn][bi] = s;
      setBeads(bs.map((t) => [...t]));
      beadTap();
      if (!fast) await sleep(140);
    }
    let cut = false;
    const p = bs[turn][bi];
    if (p < HOME) {
      const [r, c] = PATHS[turn][p];
      const key = `${r},${c}`;
      if (!SAFE.has(key)) {
        for (let op = 0; op < count; op++) {
          if (op === turn) continue;
          bs[op].forEach((opP, oi) => {
            if (opP > 0 && opP < HOME) {
              const [orr, occ] = PATHS[op][opP];
              if (`${orr},${occ}` === key) {
                bs[op][oi] = 0;
                cut = true;
                toast(`ਕੱਟ! ${FOODS[turn].name} cut ${FOODS[op].name} back to its box — throw again!`,
                  { style: { background: "#2A1206", color: "#E25822" } });
              }
            }
          });
        }
        if (cut) setBeads(bs.map((t) => [...t]));
      }
    }
    if (p === HOME && !fast) toast(`${FOODS[turn].name} bead reached the centre ghar!`, { style: { background: "#1A1208", color: "#D4AF37" } });
    if (beadRef.current[turn].every((pos) => pos >= HOME)) {
      const wn = FOODS[turn].name;
      scoreRef.current[turn] = (scoreRef.current[turn] || 0) + 1;
      setScore([...scoreRef.current]);
      setWinner(wn);
      setPhase(mode === "bo3" && scoreRef.current[turn] < 2 ? "round-over" : "won");
      if (!(mode === "bo3" && scoreRef.current[turn] < 2)) coinRain();
      finishOnServer(wn, mode === "bo3" && scoreRef.current[turn] >= 2);
    } else if (!(v === 4 || v === 8 || cut)) {
      setTurn((t) => (t + 1) % count);
    }
    setAnim(false);
  };

  const roll = async () => {
    if (anim || rolling || pending || phase !== "playing") return;
    setRolling(true);
    shellClatter();
    const fast = reduced();
    if (!fast) {
      for (let f = 0; f < 3; f++) {
        setShells(Array.from({ length: 4 }, () => Math.random() < 0.5));
        await sleep(160);
      }
    }
    const final = Array.from({ length: 4 }, () => Math.random() < 0.5);
    setShells(final);
    const ups = final.filter(Boolean).length;
    const v = ups === 0 ? 8 : ups;
    setRollVal(v);
    setRolling(false);
    if (v === 8) toast("ਅਸ਼ਟਾ! All four flat — 8 steps and another throw!", { style: { background: "#1A1208", color: "#F0C742" } });
    const movable = beadRef.current[turn]
      .map((p, i) => ({ p, i }))
      .filter((t) => t.p < HOME && t.p + v <= HOME);
    if (!movable.length) {
      toast(`${FOODS[turn].name} has no move — needs an exact throw to enter the ghar`, { style: { background: "#1A1208", color: "#ffffff99" } });
      if (!(v === 4 || v === 8)) setTurn((t) => (t + 1) % count);
      return;
    }
    if (movable.length === 1 || movable.every((m) => m.p === movable[0].p)) {
      await moveBead(movable[0].i, v);
    } else {
      setPending({ v, movable: movable.map((m) => m.i) });
    }
  };

  const boardBeads = [];
  beads.forEach((bs, pl) => bs.forEach((p, bi) => boardBeads.push({ pl, bi, p })));
  const cellGroups = {};
  boardBeads.forEach((b) => {
    const [r, c] = PATHS[b.pl][Math.min(b.p, HOME)];
    const key = `${r},${c}`;
    b.cell = key; b.r = r; b.c = c;
    b.slot = (cellGroups[key] = (cellGroups[key] || 0)) ; cellGroups[key]++;
  });
  const OFF = [[-0.42, -0.42], [0.42, -0.42], [-0.42, 0.42], [0.42, 0.42], [0, 0], [-0.42, 0], [0.42, 0], [0, -0.42], [0, 0.42]];

  return (
    <div data-testid="game-punjabi-ludo" className="rounded-2xl bg-[#121212] border border-[#F97316]/40 p-5">
      <style>{`
        .kaudi-shell{width:30px;height:40px;display:inline-block;position:relative;border-radius:50% 50% 50% 50%/58% 58% 42% 42%;transition:transform .15s}
        .kaudi-up{background:radial-gradient(ellipse at 50% 32%,#FBF3DF 0%,#EBDDBC 55%,#C9B489 100%);box-shadow:inset 0 -3px 6px rgba(120,95,50,.45),0 2px 4px rgba(0,0,0,.5)}
        .kaudi-down{background:radial-gradient(ellipse at 38% 26%,#FFF8E8 0%,#E8D9B4 45%,#B49B6C 100%);box-shadow:inset 0 3px 5px rgba(255,255,255,.6),inset 0 -4px 7px rgba(110,85,40,.5),0 2px 4px rgba(0,0,0,.5)}
        .kaudi-mouth{position:absolute;left:50%;top:14%;bottom:16%;width:7px;transform:translateX(-50%);border-radius:5px;background:repeating-linear-gradient(180deg,#8a734a 0 2px,#5d4a28 2px 3px,#c9b489 3px 6px)}
        .kaudi-rolling{animation:kaudiTumble .45s ease infinite}
        @keyframes kaudiTumble{0%{transform:translateY(0) rotate(0)}30%{transform:translateY(-9px) rotate(24deg)}60%{transform:translateY(2px) rotate(-18deg)}100%{transform:translateY(0) rotate(0)}}
        .kaudi-x{background-image:linear-gradient(45deg,transparent calc(50% - 1px),#3A2E1E calc(50% - 1px),#3A2E1E calc(50% + 1px),transparent calc(50% + 1px)),linear-gradient(-45deg,transparent calc(50% - 1px),#3A2E1E calc(50% - 1px),#3A2E1E calc(50% + 1px),transparent calc(50% + 1px))}
        .kaudi-bead{border-radius:9999px;border:1.5px solid rgba(0,0,0,.55);box-shadow:inset -2px -3px 4px rgba(0,0,0,.35),inset 2px 2px 3px rgba(255,255,255,.5),0 2px 3px rgba(0,0,0,.45)}
        @media (prefers-reduced-motion: reduce){.kaudi-rolling{animation:none}}
      `}</style>
      <p className="text-[10px] tracking-[0.3em] text-[#F97316] font-bold">KAUDI CHAUPAR · ਕੌਡੀ ਚੌਪੜ</p>
      <p className="text-xs text-white/50 mt-1">The Punjabi veranda board — throw 4 kaudi shells, race your beads to the centre ghar. Winner takes 40 Regal Coins for this passport.</p>

      {howto && (
        <div data-testid="ludo-howto" className="mt-4 rounded-xl border border-[#D4AF37]/30 bg-[#1A1208] p-4">
          <p className="text-[9px] tracking-[0.25em] text-[#D4AF37] font-black">HOW TO PLAY · GHAR STYLE RULES</p>
          <p className="text-xs text-white/70 mt-1.5 leading-relaxed">
            Throw the 4 kaudi shells — mouths facing up are your steps (1–4). All four flat is <b>ASHTA — 8 steps!</b> A
            4 or 8 earns another throw. Beads circle the outer lane, turn into the inner lane, and need an <b>exact throw</b> to
            enter the centre ghar. Land on a rival's bead on a plain square and it's <b>cut</b> — back to its box, and you throw
            again. X squares are safe. First to bring every bead home wins!
          </p>
          <button data-testid="ludo-howto-ok" onClick={() => { localStorage.setItem(HOWTO_KEY, "1"); setHowto(false); }}
            className="mt-3 h-9 px-5 rounded-full bg-[#D4AF37] text-black text-[10px] font-black tracking-widest">GOT IT</button>
        </div>
      )}

      {!howto && phase === "setup" && (
        <div data-testid="ludo-setup" className="mt-4">
          <p className="text-[10px] tracking-widest text-white/50 font-black">PLAYERS AT THE TABLE</p>
          <div className="flex gap-2 mt-2">
            {[2, 3, 4].map((n) => (
              <button key={n} data-testid={`ludo-count-${n}`} onClick={() => setCount(n)}
                className={`h-10 px-5 rounded-full border text-xs font-black ${count === n ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]" : "border-white/15 text-white/50"}`}>{n}</button>
            ))}
          </div>
          <p className="text-[10px] tracking-widest text-white/50 font-black mt-3">BEADS EACH</p>
          <div className="flex gap-2 mt-2">
            {[[2, "QUICK · 2"], [4, "TRADITIONAL · 4"]].map(([n, label]) => (
              <button key={n} data-testid={`ludo-beads-${n}`} onClick={() => setBeadCount(n)}
                className={`h-10 px-5 rounded-full border text-[10px] font-black tracking-widest ${beadCount === n ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]" : "border-white/15 text-white/50"}`}>{label}</button>
            ))}
          </div>
          <p className="text-[10px] tracking-widest text-white/50 font-black mt-3">MATCH</p>
          <div className="flex gap-2 mt-2">
            {[["single", "SINGLE ROUND"], ["bo3", "BEST OF 3"]].map(([m, label]) => (
              <button key={m} data-testid={`ludo-mode-${m}`} onClick={() => setMode(m)}
                className={`h-10 px-5 rounded-full border text-[10px] font-black tracking-widest ${mode === m ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#D4AF37]" : "border-white/15 text-white/50"}`}>{label}</button>
            ))}
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {FOODS.slice(0, count).map((f, i) => (
              <span key={i} className="flex items-center gap-1.5 rounded-full border border-white/10 px-3 py-1.5 text-[10px] font-black" style={{ color: f.color }}>
                <span className="kaudi-bead inline-block w-3 h-3" style={{ background: `radial-gradient(circle at 35% 30%, #fff8, ${f.color} 45%, ${f.dark})` }} /> {f.name.toUpperCase()} · {f.pa}
              </span>
            ))}
          </div>
          <button data-testid="ludo-start" onClick={start} disabled={busy}
            className="mt-4 w-full h-12 rounded-full bg-[#F97316] text-black font-black text-sm tracking-widest disabled:opacity-50 flex items-center justify-center gap-2">
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Shell size={16} />} LAY OUT THE BOARD
          </button>
        </div>
      )}

      {phase !== "setup" && (
        <>
          <div className="flex gap-1.5 flex-wrap mt-4">
            {FOODS.slice(0, count).map((f, i) => (
              <span key={i} data-testid={`ludo-player-${i}`}
                className={`flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-black ${i === turn && phase === "playing" ? "border-[#D4AF37] bg-[#D4AF37]/10" : "border-white/10 text-white/50"}`}>
                <span className="kaudi-bead inline-block w-2.5 h-2.5" style={{ background: `radial-gradient(circle at 35% 30%, #fff8, ${f.color} 45%, ${f.dark})` }} />
                {f.name.toUpperCase()}
                {mode === "bo3" && <span className="text-[#F0C742] tracking-tighter">{"●".repeat(score[i] || 0)}</span>}
                <span className="text-white/40">{(beads[i] || []).filter((p) => p >= HOME).length}/{beadCount} ghar</span>
              </span>
            ))}
          </div>

          <div className="mt-3 lg:flex lg:items-start lg:gap-5">
            {/* cloth board */}
            <div className="mx-auto w-full max-w-[min(92vw,420px)] lg:max-w-[400px] lg:shrink-0 rounded-lg p-3 sm:p-4 relative"
              style={{ background: "linear-gradient(160deg,#F4EAD5,#EADCBB)", boxShadow: "inset 0 0 30px rgba(120,95,50,.25), 0 6px 18px rgba(0,0,0,.5)" }}>
              <div className="flex items-center justify-between px-1">
                <Kaudi up />
                <p className="text-center text-[#241C12]">
                  <span className="font-display font-bold text-base leading-none">ਕੌਡੀ ਚੌਪੜ</span>
                  <span className="block text-[8px] tracking-[0.35em] font-black text-[#241C12]/60">KAUDI CHAUPAR</span>
                </p>
                <Kaudi up={false} />
              </div>
              <div className="flex items-stretch gap-1 mt-2">
                <p className="text-[7px] tracking-[0.3em] font-black text-[#241C12]/50 self-center" style={{ writingMode: "vertical-rl", transform: "rotate(180deg)" }}>ਸਬਰ · PATIENCE</p>
                <div className="relative flex-1 border-2 border-[#3A2E1E]" style={{ background: "#F7EFDC" }}>
                  <div className="grid" style={{ gridTemplateColumns: "repeat(5, 1fr)" }}>
                    {Array.from({ length: 25 }, (_, ci) => {
                      const r = Math.floor(ci / 5), c = ci % 5;
                      const key = `${r},${c}`;
                      const startPl = START_KEYS.indexOf(key);
                      const isX = SAFE.has(key) && key !== "2,2";
                      const isCenter = key === "2,2";
                      return (
                        <div key={ci} className={`aspect-square border border-[#3A2E1E] relative ${isX ? "kaudi-x" : ""}`}
                          style={startPl >= 0 && startPl < count ? { boxShadow: `inset 0 0 0 2.5px ${FOODS[startPl].color}` } : undefined}>
                          {isCenter && (
                            <span className="absolute inset-0 flex items-center justify-center gap-[2px] opacity-80" aria-hidden="true">
                              <span className="kaudi-shell kaudi-down" style={{ width: 9, height: 13 }} />
                              <span className="kaudi-shell kaudi-up" style={{ width: 9, height: 13 }} />
                              <span className="kaudi-shell kaudi-down" style={{ width: 9, height: 13 }} />
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  {boardBeads.map((b) => {
                    const selectable = pending && b.pl === turn && b.p < HOME && pending.movable.includes(b.bi);
                    const [or_, oc] = OFF[b.slot % OFF.length];
                    const F = FOODS[b.pl];
                    return (
                      <button key={`${b.pl}-${b.bi}`} data-testid={`ludo-token-${b.pl}-${b.bi}`}
                        onClick={() => selectable && moveBead(b.bi, pending.v)}
                        disabled={!selectable}
                        aria-label={`${F.name} bead ${b.bi + 1}`}
                        className={`kaudi-bead absolute w-[7.5%] h-[7.5%] -translate-x-1/2 -translate-y-1/2 ${selectable ? "animate-pulse ring-2 ring-[#241C12] cursor-pointer" : ""} ${b.p >= HOME ? "opacity-80" : ""}`}
                        style={{
                          left: `${(b.c + 0.5 + oc * 0.28) * 20}%`,
                          top: `${(b.r + 0.5 + or_ * 0.28) * 20}%`,
                          background: `radial-gradient(circle at 35% 30%, #fff9, ${F.color} 45%, ${F.dark})`,
                          zIndex: b.pl === turn ? 8 : 5,
                        }} />
                    );
                  })}
                </div>
                <p className="text-[7px] tracking-[0.3em] font-black text-[#241C12]/50 self-center" style={{ writingMode: "vertical-rl" }}>ਚਾਲ · STRATEGY</p>
              </div>
              <div className="flex items-center justify-between px-1 mt-2">
                <Kaudi up={false} />
                <p className="text-[8px] tracking-[0.35em] font-black text-[#241C12]/60">ਪੁਰਾਣਾ ਪੰਜਾਬ · GHAR STYLE</p>
                <Kaudi up />
              </div>
            </div>

            {/* shells + controls */}
            <div className="mt-4 lg:mt-0 lg:flex-1">
              <div className="rounded-xl border border-[#8a6d1f]/40 bg-[#1A1208] p-4 text-center">
                <p className="text-[9px] tracking-[0.3em] text-[#D4AF37]/70 font-black">THE FOUR KAUDIS</p>
                <div data-testid="ludo-shells" className="flex items-center justify-center gap-3 mt-3">
                  {shells.map((up, i) => <Kaudi key={i} up={up} rolling={rolling} />)}
                </div>
                {rollVal && !rolling && (
                  <p data-testid="ludo-result" className={`mt-2 text-sm font-black ${rollVal >= 4 ? "text-[#F0C742]" : "text-white/80"}`}>{ROLL_NAME[rollVal]}</p>
                )}
              </div>

              {phase === "playing" && (
                pending ? (
                  <p data-testid="ludo-pick-hint" className="mt-3 text-center text-xs font-black text-[#D4AF37] animate-pulse">
                    {FOODS[turn].name.toUpperCase()} THREW {pending.v} — TAP A BEAD TO MOVE
                  </p>
                ) : (
                  <button data-testid="ludo-roll" onClick={roll} disabled={anim || rolling}
                    className="mt-3 w-full h-12 rounded-full bg-[#D4AF37] text-black font-black text-sm tracking-widest disabled:opacity-40 flex items-center justify-center gap-2">
                    <Shell size={16} /> {anim || rolling ? "…" : `${FOODS[turn].name.toUpperCase()} — THROW THE KAUDIS`}
                  </button>
                )
              )}

              {phase === "round-over" && (
                <div data-testid="ludo-round-over" className="mt-3 rounded-xl border border-[#D4AF37]/50 bg-[#1C160A] p-4 text-center">
                  <p className="text-[9px] tracking-[0.3em] text-[#D4AF37]/70 font-black">ROUND {score.reduce((a, b) => a + b, 0)} OF 3</p>
                  <p className="font-display text-xl font-bold mt-1">{winner} takes the round!</p>
                  <div data-testid="ludo-scorecard" className="flex justify-center gap-2 mt-2 flex-wrap">
                    {FOODS.slice(0, count).map((f, i) => (
                      <span key={i} className="rounded-full border border-white/10 px-3 py-1 text-[10px] font-black" style={{ color: f.color }}>
                        {f.name.toUpperCase()} {"●".repeat(score[i] || 0) || "○"}
                      </span>
                    ))}
                  </div>
                  {reward?.message && (
                    <p data-testid="ludo-round-reward" className={`text-xs font-bold mt-1.5 flex items-center justify-center gap-1.5 ${reward.error ? "text-white/45" : "text-[#F0C742]"}`}>{!reward.error && <MelaCoin size={14} />} {reward.message}</p>
                  )}
                  <button data-testid="ludo-next-round" onClick={nextRound} disabled={busy}
                    className="mt-3 h-10 px-6 rounded-full bg-[#D4AF37] text-black text-[10px] font-black tracking-widest disabled:opacity-50">
                    {busy ? "…" : "NEXT ROUND"}
                  </button>
                </div>
              )}

              {phase === "won" && (
                <div data-testid="ludo-won" className="mt-3 rounded-xl border border-[#D4AF37]/50 bg-[#1C160A] p-4 text-center">
                  <PartyPopper size={22} className="mx-auto text-[#D4AF37]" />
                  <p className="font-display text-2xl font-bold mt-1">{winner} wins the {mode === "bo3" ? "match" : "chaupar"}!</p>
                  <p className="text-xs text-white/55 mt-0.5">ਵਧਾਈਆਂ! Every bead reached the centre ghar.</p>
                  {mode === "bo3" && (
                    <div data-testid="ludo-final-scorecard" className="flex justify-center gap-2 mt-2 flex-wrap">
                      {FOODS.slice(0, count).map((f, i) => (
                        <span key={i} className="rounded-full border border-white/10 px-3 py-1 text-[10px] font-black" style={{ color: f.color }}>
                          {f.name.toUpperCase()} {"●".repeat(score[i] || 0) || "○"}
                        </span>
                      ))}
                    </div>
                  )}
                  {reward?.message && (
                    <p data-testid="ludo-reward" className={`text-xs font-bold mt-1.5 flex items-center justify-center gap-1.5 ${reward.error ? "text-white/45" : "text-[#F0C742]"}`}>{!reward.error && <MelaCoin size={14} />} {reward.message}</p>
                  )}
                  <button data-testid="ludo-again" onClick={() => setPhase("setup")}
                    className="mt-3 h-10 px-6 rounded-full border border-[#F97316]/70 text-[#F97316] text-[10px] font-black tracking-widest">PLAY AGAIN</button>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
